// Builds the argv array (no shell) for `claude -p`. Prompt is NOT part of
// argv — it goes via stdin (session-runner's job).
//
// `--allowedTools <rules>`: rules = spec.allowedTools, joined as ONE string
// arg. Claude CLI convention (research/claude-headless-contract.md §7) shows
// space-separated rule tokens inside a single flag value, e.g.
// `--allowedTools "Bash(git *)" Read`. We join with a single space — same
// source array that becomes binding.allowed_tools (test 53 cross-checks this
// equality), so the join must be reversible/stable but is never re-parsed by
// this module itself.
import { AUTOPILOT_RESULT_JSON_SCHEMA_STRING } from "./autopilot-result-schema.js";

export interface ArgvBuilderSpec {
  sessionId: string;
  allowedTools: string[];
  workerModel?: string;
}

export interface ArgvBuilderConfig {
  workerModel?: string; // config-level default (worker_model), spec.workerModel wins
}

const DEFAULT_WORKER_MODEL = "claude-sonnet-5";

// 06g R4 (audit H1): the worker keeps the operator's HOME, so without this
// flag `claude -p` also loads `~/.claude/settings.json` (+ settings.local.json)
// — a personal `permissions.allow`, `hooks`, `env` or `apiKeyHelper` would
// reach the worker. `project` = the mission's own `.claude/settings.json` is
// the single settings source (measured on claude 2.1.293: the flag exists;
// a user-level apiKeyHelper is no longer honoured — doctor warns about it).
export const WORKER_SETTING_SOURCES = "project";
/** The same choice as one token, for `compatibility.json`'s `supported_flags`. */
export const WORKER_SETTING_SOURCES_FLAG = `--setting-sources=${WORKER_SETTING_SOURCES}`;

export class ContractError extends Error {
  readonly code = "contract_error";
  constructor(message: string) {
    super(message);
    this.name = "ContractError";
  }
}

// Throws ContractError BEFORE building spawn args if allowedTools missing/empty.
export function buildClaudeHeadlessArgv(
  spec: ArgvBuilderSpec,
  config: ArgvBuilderConfig = {},
): string[] {
  if (!spec.allowedTools || spec.allowedTools.length === 0) {
    throw new ContractError(
      "claude-headless-argv-builder: spec.allowedTools rỗng/thiếu — không tự suy rules, từ chối build argv trước spawn.",
    );
  }

  const model = spec.workerModel ?? config.workerModel ?? DEFAULT_WORKER_MODEL;
  const allowedToolsArg = spec.allowedTools.join(" ");

  return [
    "-p",
    "--output-format",
    "stream-json",
    "--verbose",
    "--include-hook-events",
    "--json-schema",
    AUTOPILOT_RESULT_JSON_SCHEMA_STRING,
    "--permission-mode",
    "dontAsk",
    "--allowedTools",
    allowedToolsArg,
    // Re-review 2026-09-26 (C1-F3): the worker keeps the operator's real HOME
    // (Keychain login), so without this flag `claude -p` also loads every
    // USER-level MCP server from ~/.claude.json — tools outside any adapter's
    // allowedTools, servers running with the operator's credentials. Strict
    // + no `--mcp-config` of our own == zero MCP servers in the worker.
    "--strict-mcp-config",
    "--setting-sources",
    WORKER_SETTING_SOURCES,
    "--session-id",
    spec.sessionId,
    "--model",
    model,
  ];
}
